/* See UserWizardProfileStep: labels are associated explicitly via htmlFor + the
   control's DOM id, so the hint text next to a field stays outside the <label>. */
/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "htmlFor" }] */
import React, { useEffect, useRef } from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { ControlledInput } from 'src/components/ui/Input/Input';
import { getInitials, getPasswordStrength } from 'src/utils/general';
import { PASSWORD_HINT_KEYS, PermissionLevels, buildPermissionSummary } from '../userWizardLogic';
import { CredentialsStepValues } from '../userWizardSchema';

type Props = {
  control: Control<CredentialsStepValues>;
  /** Values collected in step 1, shown in the recap card. */
  name: string;
  permissions: PermissionLevels;
  password: string;
  onEditProfile: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
};

/**
 * Colour per reached score. Kept as CSS variables so the meter flips with the
 * theme; there is no solid utility class for the warning colour.
 */
const STRENGTH_COLORS = ['var(--status-error-full)', 'var(--status-warning-full)', 'var(--status-success-full)'];
const EMPTY_SEGMENT_COLOR = 'var(--surface-active)';

const EMAIL_INPUT_ID = 'user-wizard-email';
const PASSWORD_INPUT_ID = 'user-wizard-password';

const UserWizardCredentialsStep: React.FC<Props> = ({
  control,
  name,
  permissions,
  password,
  onEditProfile,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  const strength = getPasswordStrength(password);
  const displayName = name.trim() || t('general.user_wizard.unnamed_user');

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-32">
      <div className="surface-base border-subtle rounded-base flex items-center gap-16 p-16">
        <span
          aria-hidden
          className="surface-elevated border-subtle flex h-[4rem] w-[4rem] flex-shrink-0 items-center justify-center rounded-full typo-text-14--bold text-full">
          {getInitials(name)}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <span className="typo-text-14--bold text-full truncate">{displayName}</span>
          <span className="typo-text-12 text-faded">{buildPermissionSummary(t, permissions)}</span>
        </div>
        <Button
          tone={ButtonToneProps.GHOST}
          title={t('general.edit')}
          onClick={onEditProfile}
          className="flex-shrink-0 underline underline-offset-2"
        />
      </div>

      <div className="flex max-w-460 flex-col gap-8">
        <label htmlFor={EMAIL_INPUT_ID} className="typo-text-14--bold text-full">
          {t('general.email')}
        </label>
        <ControlledInput
          ref={emailInputRef}
          id={EMAIL_INPUT_ID}
          control={control}
          name="email"
          type="email"
          placeholder={t('general.user_wizard.email_placeholder')}
          autoComplete="off"
        />
        <span className="typo-text-12 text-faded">{t('general.user_wizard.email_hint')}</span>
      </div>

      <div className="flex max-w-460 flex-col gap-8">
        <label htmlFor={PASSWORD_INPUT_ID} className="typo-text-14--bold text-full">
          {t('general.initial_password')}
        </label>
        <ControlledInput
          id={PASSWORD_INPUT_ID}
          control={control}
          name="password"
          icon="lock"
          secret
          type="password"
          placeholder={t('general.user_wizard.password_placeholder')}
          autoComplete="new-password"
        />
        <div aria-hidden className="flex gap-6">
          {[0, 1, 2].map(segment => (
            <span
              key={segment}
              className="h-[0.4rem] flex-1 rounded-full"
              style={{
                backgroundColor: strength > segment ? STRENGTH_COLORS[strength - 1] : EMPTY_SEGMENT_COLOR,
              }}
            />
          ))}
        </div>
        {/* The meter itself is decorative; this line is what assistive tech announces. */}
        <p role="status" className="typo-text-12 text-faded">
          {t(PASSWORD_HINT_KEYS[strength])}
        </p>
      </div>
    </form>
  );
};

export default UserWizardCredentialsStep;
